import type { CSSProperties, ReactNode } from 'react';
import { useViewport } from '@/lib/viewport';

export interface Column<T> {
  header: ReactNode;
  key?: keyof T & string;
  render?: (row: T, i: number) => ReactNode;
  width?: number | string;
  align?: 'left' | 'right' | 'center';
  mono?: boolean;
  numeric?: boolean;
  muted?: boolean;
  wrap?: boolean;
  priority?: 1 | 2 | 3; // 1=always, 2=hide mobile, 3=hide mobile+tablet
}

interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getRowKey?: (row: T, i: number) => string | number;
  dense?: boolean;
  onRowClick?: (row: T) => void;
  emptyText?: string;
  stickyHeader?: boolean;
}

export function Table<T>({
  columns,
  rows,
  getRowKey,
  dense,
  onRowClick,
  emptyText = 'Chưa có dữ liệu',
  stickyHeader,
}: TableProps<T>) {
  const { isMobile, isTablet } = useViewport();
  const visibleCols = columns.filter((c) => {
    const p = c.priority ?? 1;
    if (isMobile && p >= 2) return false;
    if (isTablet && p >= 3) return false;
    return true;
  });

  if (!rows || rows.length === 0) {
    return (
      <div
        style={{
          padding: 32,
          textAlign: 'center',
          color: 'var(--bx-text-muted)',
          fontSize: 12.5,
        }}
      >
        {emptyText}
      </div>
    );
  }

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: 0,
          fontSize: 13,
          color: 'var(--bx-text-body)',
        }}
      >
        <thead>
          <tr>
            {visibleCols.map((c, i) => {
              const thStyle: CSSProperties = {
                position: stickyHeader ? 'sticky' : undefined,
                top: stickyHeader ? 0 : undefined,
                background: 'var(--bx-surface-2)',
                textAlign: c.align ?? 'left',
                padding: dense ? '8px 10px' : '10px 12px',
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--bx-text-muted)',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                borderBottom: '1px solid var(--bx-border-muted)',
                whiteSpace: 'nowrap',
                width: c.width,
              };
              return (
                <th key={(c.key as string) ?? i} style={thStyle}>
                  {c.header}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={getRowKey ? getRowKey(r, i) : i}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
              style={{
                background: i % 2 === 1 ? 'rgba(245,245,245,0.4)' : '#fff',
                cursor: onRowClick ? 'pointer' : 'default',
              }}
            >
              {visibleCols.map((c, ci) => {
                const tdStyle: CSSProperties = {
                  padding: dense ? '8px 10px' : '11px 12px',
                  textAlign: c.align ?? 'left',
                  borderBottom: '1px solid var(--bx-border-muted)',
                  color: c.muted ? 'var(--bx-text-muted)' : 'var(--bx-text-dark)',
                  fontFamily: c.mono ? 'var(--bx-font-mono)' : 'inherit',
                  fontVariantNumeric: c.mono || c.numeric ? 'tabular-nums' : 'normal',
                  whiteSpace: c.wrap ? 'normal' : 'nowrap',
                };
                const value = c.render
                  ? c.render(r, i)
                  : c.key
                    ? (r[c.key] as ReactNode)
                    : null;
                return (
                  <td key={(c.key as string) ?? ci} style={tdStyle}>
                    {value as ReactNode}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
